<script lang="ts">
  import type { EChartsCoreOption } from 'echarts/core';
  import { onMount } from 'svelte';
  import Skeleton from '../ui/Skeleton.svelte';
  import { dimensionColorInfo } from './dimension-colors';
  import { chartThemePatch } from './options';

  let {
    option,
    ariaLabel,
    height = '20rem',
    onSelect,
  }: {
    option: EChartsCoreOption;
    ariaLabel: string;
    height?: string;
    onSelect?: (seriesId: string, bucketIndex: number) => void;
  } = $props();

  let container: HTMLDivElement;
  let instance: import('echarts/core').EChartsType | undefined = $state();
  let loaded = $state(false);
  let reduceMotion = $state(false);
  let legendSelection = $state<Record<string, boolean>>({});
  let previousLegendSignature = $state('');
  let highlightedSeriesName = $state<string | null>(null);
  let tooltipHighlightFrame = 0;
  let chartPointerSeriesName: string | null = null;
  let legendPointerSeriesName: string | null = null;
  let legendFocusSeriesName: string | null = null;

  interface LegendItem {
    name: string;
    color: string;
    emoji: string | null;
    sourceHue: number;
    sourceHues: readonly number[];
    seriesIndex: number;
    dataIndex?: number;
  }

  let legendItems = $derived.by(() => {
    const record = option as Record<string, unknown>;
    if (!Array.isArray(record.series)) return [];
    const palette = Array.isArray(record.color)
      ? record.color.filter((color): color is string => typeof color === 'string')
      : [];
    const items: LegendItem[] = [];
    const add = (
      name: unknown,
      preferredColor: unknown,
      index: number,
      seriesIndex: number,
      dataIndex?: number,
    ) => {
      if (typeof name !== 'string' || !name.trim() || items.some((item) => item.name === name))
        return;
      const info = dimensionColorInfo(`legend:${name}`, name);
      items.push({
        name,
        color: typeof preferredColor === 'string' ? preferredColor : (palette[index] ?? info.color),
        emoji: info.emoji,
        sourceHue: info.sourceHue,
        sourceHues: info.sourceHues,
        seriesIndex,
        ...(dataIndex === undefined ? {} : { dataIndex }),
      });
    };
    record.series.forEach((rawSeries, seriesIndex) => {
      if (!rawSeries || typeof rawSeries !== 'object') return;
      const series = rawSeries as Record<string, unknown>;
      const itemStyle =
        series.itemStyle && typeof series.itemStyle === 'object'
          ? (series.itemStyle as Record<string, unknown>)
          : {};
      const lineStyle =
        series.lineStyle && typeof series.lineStyle === 'object'
          ? (series.lineStyle as Record<string, unknown>)
          : {};
      add(series.name, lineStyle.color ?? itemStyle.color, seriesIndex, seriesIndex);
      if (series.type !== 'pie' || !Array.isArray(series.data)) return;
      series.data.forEach((rawDatum, dataIndex) => {
        if (!rawDatum || typeof rawDatum !== 'object') return;
        const datum = rawDatum as Record<string, unknown>;
        const datumStyle =
          datum.itemStyle && typeof datum.itemStyle === 'object'
            ? (datum.itemStyle as Record<string, unknown>)
            : {};
        add(datum.name, datumStyle.color, dataIndex, seriesIndex, dataIndex);
      });
    });
    return items;
  });
  let hasSeriesControls = $derived(legendItems.length > 1);
  let visibleSeriesCount = $derived(
    legendItems.filter((item) => legendSelection[item.name] !== false).length,
  );

  $effect(() => {
    const signature = legendItems.map((item) => item.name).join('\u0000');
    if (signature === previousLegendSignature) return;
    previousLegendSignature = signature;
    legendSelection = Object.fromEntries(legendItems.map((item) => [item.name, true]));
  });

  function configuredOption(source: EChartsCoreOption): EChartsCoreOption {
    const record = source as Record<string, unknown>;
    const currentAria =
      record.aria && typeof record.aria === 'object'
        ? (record.aria as Record<string, unknown>)
        : {};
    const configured: EChartsCoreOption = {
      ...source,
      aria: { ...currentAria, enabled: true, description: ariaLabel },
      legend: hasSeriesControls
        ? {
            ...(record.legend && !Array.isArray(record.legend) && typeof record.legend === 'object'
              ? (record.legend as Record<string, unknown>)
              : {}),
            show: false,
            type: 'plain',
            data: legendItems.map((item) => item.name),
            selected: legendSelection,
          }
        : source.legend,
    };
    return reduceMotion
      ? {
          ...configured,
          animation: false,
          animationDuration: 0,
          animationDurationUpdate: 0,
          stateAnimation: { duration: 0 },
        }
      : configured;
  }

  function updateTheme(): void {
    if (!instance) return;
    instance.setOption(chartThemePatch(option));
  }

  function syncTooltipHighlight(): void {
    cancelAnimationFrame(tooltipHighlightFrame);
    tooltipHighlightFrame = requestAnimationFrame(() => {
      const rows = container?.querySelectorAll<HTMLElement>('[data-tooltip-series]') ?? [];
      rows.forEach((row) => {
        const isHighlighted = row.dataset.tooltipSeries === highlightedSeriesName;
        row.classList.toggle('is-highlighted', isHighlighted);
        row.classList.toggle('is-muted', highlightedSeriesName !== null && !isHighlighted);
      });
    });
  }

  function dispatchSeriesState(type: 'highlight' | 'downplay', name: string): void {
    const item = legendItems.find((candidate) => candidate.name === name);
    if (!instance || !item) return;
    instance.dispatchAction({
      type,
      seriesIndex: item.seriesIndex,
      ...(item.dataIndex === undefined ? {} : { dataIndex: item.dataIndex }),
    });
  }

  function highlightSeries(name: string | null): void {
    if (name === highlightedSeriesName) {
      syncTooltipHighlight();
      return;
    }
    if (highlightedSeriesName) dispatchSeriesState('downplay', highlightedSeriesName);
    highlightedSeriesName = name;
    if (name) dispatchSeriesState('highlight', name);
    syncTooltipHighlight();
  }

  function reconcileSeriesHighlight(): void {
    const candidate = legendPointerSeriesName ?? chartPointerSeriesName ?? legendFocusSeriesName;
    highlightSeries(candidate && legendSelection[candidate] !== false ? candidate : null);
  }

  function setChartPointerSeries(name: string | null): void {
    chartPointerSeriesName = name;
    reconcileSeriesHighlight();
  }

  function setLegendPointerSeries(name: string | null): void {
    legendPointerSeriesName = name;
    reconcileSeriesHighlight();
  }

  function setLegendFocusSeries(name: string | null): void {
    legendFocusSeriesName = name;
    reconcileSeriesHighlight();
  }

  function legendNameFromChartEvent(event: unknown): string | null {
    const point = event as {
      seriesIndex?: number;
      dataIndex?: number;
      seriesName?: string;
      name?: string;
    };
    const exactName = point.seriesName || point.name;
    if (exactName && legendItems.some((item) => item.name === exactName)) return exactName;
    const item = legendItems.find(
      (candidate) =>
        candidate.seriesIndex === point.seriesIndex &&
        (candidate.dataIndex === undefined || candidate.dataIndex === point.dataIndex),
    );
    return item?.name ?? null;
  }

  function updateLegend(next: Record<string, boolean>): void {
    legendSelection = next;
    reconcileSeriesHighlight();
    instance?.setOption({ legend: { selected: next } });
  }

  function toggleSeries(name: string): void {
    updateLegend({ ...legendSelection, [name]: legendSelection[name] === false });
  }

  function selectAllSeries(): void {
    updateLegend(Object.fromEntries(legendItems.map((item) => [item.name, true])));
  }

  function hideAllSeries(): void {
    updateLegend(Object.fromEntries(legendItems.map((item) => [item.name, false])));
  }

  function isolateSeries(name: string): void {
    updateLegend(Object.fromEntries(legendItems.map((item) => [item.name, item.name === name])));
  }

  function invertSeries(): void {
    updateLegend(
      Object.fromEntries(
        legendItems.map((item) => [item.name, legendSelection[item.name] === false]),
      ),
    );
  }

  $effect(() => {
    if (instance)
      instance.setOption(configuredOption(option), { notMerge: true, lazyUpdate: true });
  });

  onMount(() => {
    let active = true;
    let frame = 0;
    let resizeObserver: ResizeObserver | undefined;
    let visibilityObserver: IntersectionObserver | undefined;
    let tooltipObserver: MutationObserver | undefined;
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotion = () => (reduceMotion = motionQuery.matches);
    updateMotion();
    motionQuery.addEventListener('change', updateMotion);
    window.addEventListener('money-theme-change', updateTheme);

    const initialize = () =>
      void import('./echarts').then(({ init }) => {
        if (!active) return;
        instance = init(container, undefined, {
          renderer: 'canvas',
          useDirtyRect: false,
          useCoarsePointer: true,
          pointerSize: 44,
        });
        instance.setOption(configuredOption(option));
        instance.on('click', (event) => {
          const point = event as { seriesId?: string | number; dataIndex?: number };
          if (point.seriesId !== undefined && point.dataIndex !== undefined) {
            onSelect?.(String(point.seriesId), point.dataIndex);
          }
        });
        instance.on('mouseover', (event) => {
          const name = legendNameFromChartEvent(event);
          if (name) setChartPointerSeries(name);
        });
        instance.on('mouseout', (event) => {
          const name = legendNameFromChartEvent(event);
          if (name === chartPointerSeriesName) setChartPointerSeries(null);
        });
        instance.getZr().on('globalout', () => setChartPointerSeries(null));
        tooltipObserver = new MutationObserver(syncTooltipHighlight);
        tooltipObserver.observe(container, { childList: true, subtree: true });
        loaded = true;
        resizeObserver = new ResizeObserver(() => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => instance?.resize());
        });
        resizeObserver.observe(container);
      });

    if ('IntersectionObserver' in window) {
      visibilityObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            visibilityObserver?.disconnect();
            initialize();
          }
        },
        { rootMargin: '320px 0px' },
      );
      visibilityObserver.observe(container);
    } else {
      initialize();
    }

    return () => {
      active = false;
      cancelAnimationFrame(frame);
      cancelAnimationFrame(tooltipHighlightFrame);
      visibilityObserver?.disconnect();
      resizeObserver?.disconnect();
      tooltipObserver?.disconnect();
      motionQuery.removeEventListener('change', updateMotion);
      window.removeEventListener('money-theme-change', updateTheme);
      instance?.dispose();
      instance = undefined;
    };
  });
</script>

<div class="chart-shell">
  {#if hasSeriesControls}
    <section class="chart-series-controls" aria-label="Visibilità delle serie nel grafico">
      <header class="chart-series-toolbar">
        <div class="chart-series-heading">
          <span>Serie</span>
          <small aria-live="polite">{visibleSeriesCount} di {legendItems.length} visibili</small>
        </div>
        <div>
          <button type="button" disabled={!loaded} onclick={selectAllSeries}>Tutte</button>
          <button type="button" disabled={!loaded} onclick={hideAllSeries}>Nessuna</button>
          <button type="button" disabled={!loaded} onclick={invertSeries}>Inverti</button>
        </div>
      </header>
      <div
        class="chart-series-list"
        class:has-highlight={highlightedSeriesName !== null}
        role="group"
        aria-label={`Serie disponibili: ${ariaLabel}`}
      >
        {#each legendItems as item (item.name)}
          <button
            class:inactive={legendSelection[item.name] === false}
            class:is-highlighted={highlightedSeriesName === item.name}
            type="button"
            disabled={!loaded}
            aria-pressed={legendSelection[item.name] !== false}
            title={`${item.name} · doppio clic per isolare`}
            data-series-name={item.name}
            data-series-color={item.color}
            data-emoji={item.emoji ?? ''}
            data-source-hue={item.sourceHue.toFixed(2)}
            data-source-hues={item.sourceHues.map((hue) => hue.toFixed(2)).join(',')}
            style={`--legend-color: ${item.color}`}
            onclick={() => toggleSeries(item.name)}
            ondblclick={() => isolateSeries(item.name)}
            onmouseenter={() => setLegendPointerSeries(item.name)}
            onmouseleave={() => setLegendPointerSeries(null)}
            onfocus={() => setLegendFocusSeries(item.name)}
            onblur={() => setLegendFocusSeries(null)}
          >
            <span class="chart-series-swatch" aria-hidden="true"></span>
            <span class="chart-series-name">{item.name}</span>
            {#if highlightedSeriesName === item.name}
              <span class="chart-series-active-state" aria-hidden="true">In analisi</span>
            {/if}
          </button>
        {/each}
      </div>
    </section>
  {/if}
  <div class="chart-frame" style:min-height={height} aria-label={ariaLabel} role="img">
    {#if !loaded}<Skeleton {height} label="Caricamento grafico" />{/if}
    <div bind:this={container} class:chart-loaded={loaded} class="chart-canvas" style:height></div>
  </div>
</div>
